/* eslint-disable no-console */
import { db } from "@/firebase";
import encryptionService from "@/services/encryptionService";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc
} from "firebase/firestore";

const state = {
  awards: [],
  allAwardData: [],
  loadingAwards: false,
  memberQualifiedAwardData: [],
  loadingTable: false
};

const actions = {
  async loadAwards({ commit, getters }) {
    commit("setLoadingTable", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const awardsRef = collection(programRef, "awards");
      const querySnapshot = await getDocs(awardsRef);

      const awards = [];
      querySnapshot.forEach(function(docSnapshot) {
        awards.push({
          id: docSnapshot.id,
          ...docSnapshot.data()
        });
      });

      commit("setAwards", awards);
    } catch (error) {
      console.error("Error loading awards:", error);
      commit("setAwards", []);
    } finally {
      commit("setLoadingTable", false);
    }
  },

  async loadAllAwardData({ commit, getters }) {
    commit("setLoadingTable", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const awardsRef = collection(programRef, "awards");
      const awardsSnapshot = await getDocs(awardsRef);

      const awardDatabuckets = [];
      awardsSnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        if (data.selectedDatabucket) {
          awardDatabuckets.push({
            awardId: docSnapshot.id,
            id: data.selectedDatabucket,
            conditions: data.conditions || [],
            image: data.image,
            award: data.award,
            participantType: data.selectedParticipantType
          });
        }
      });

      let allAwardData = [];

      for (const awardDatabucket of awardDatabuckets) {
        try {
          const databucketRef = doc(
            collection(programRef, "databuckets"),
            awardDatabucket.id
          );
          const totalResultRef = doc(
            collection(databucketRef, "results"),
            "total"
          );

          const totalsSnapshot = await getDoc(totalResultRef);
          let jsonString;
          if (totalsSnapshot.exists()) {
            jsonString = totalsSnapshot.data().csvData;
          } else {
            console.warn(`No data found for databucket ${awardDatabucket.id}`);
            continue;
          }

          const nestedArray = JSON.parse(jsonString || "[]");
          if (!nestedArray.length) continue;

          const labels = nestedArray[0];

          const camelLabels = labels.map(element => {
            const words = element.split(" ");
            return words
              .map((word, index) => {
                if (index === 0) return word.toLowerCase();
                return word.charAt(0).toUpperCase() + word.slice(1);
              })
              .join("");
          });

          const objectsArray = nestedArray.slice(1).map(row => {
            const object = {};
            camelLabels.forEach((label, index) => {
              object[label] = row[index];
            });
            object["databucketId"] = awardDatabucket.id;
            object["participantType"] = awardDatabucket.participantType;
            object["conditions"] = awardDatabucket.conditions;
            object["image"] = awardDatabucket.image;
            object["award"] = awardDatabucket.award;
            object["awardId"] = awardDatabucket.awardId;
            return object;
          });
          allAwardData.push(...objectsArray);
        } catch (error) {
          console.error(`Error processing databucket ${awardDatabucket.id}:`, error);
        }
      }

      commit("setAllAwardData", allAwardData);
    } catch (error) {
      console.error("Error loading all award data:", error);
      commit("setAllAwardData", []);
    } finally {
      commit("setLoadingTable", false);
    }
  },

  async loadMemberQualifiedAwardData({ dispatch, commit, getters }) {
    await dispatch("loadAllAwardData");
    const awardData = getters.allAwardData.filter(
      el => el.participantType === "Member"
    );

    const results = awardData.map(award => {
      let found = false;

      for (const condition of (award.conditions || [])) {
        const targetVal = award.target;
        const actualVal = award.actual;
        const diffVal = award.difference;
        const percentVal = award.percentage;

        const realStatement = condition.statement
          .replace(/__actual/gi, actualVal)
          .replace(/__target/gi, targetVal)
          .replace(/__percent/gi, percentVal)
          .replace(/__diff/gi, diffVal);

        const qualified = Function("return " + realStatement)();
        if (qualified === true) {
          found = true;
          break; // Exit loop early if condition is met
        }
      }

      return {
        ...award,
        qualified: found
      };
    });

    const matchedArray = results
      .filter(el => el.qualified === true)
      .map(award => {
        const matchingMember = getters.members.find(
          member => encryptionService.safeDecrypt(member.accountKey) === award.accountId
        );
        if (matchingMember) {
          return {
            ...award,
            fullname: matchingMember.fullname,
            email: matchingMember.email,
            memberId: matchingMember.id
          };
        }
        return null;
      })
      .filter(item => item !== null); // Remove null entries

    commit("setMemberQualifiedAwardData", matchedArray);
  },

  checkQualified(payload) {
    console.log(payload);
    let found = false;
    const conditions = payload.conditions;

    for (const condition of conditions) {
      const targetVal = payload.target;
      const actualVal = payload.actual;
      const diffVal = payload.difference;
      const percentVal = payload.percentage;

      const realStatement = condition.statement
        .replace(/__actual/gi, actualVal)
        .replace(/__target/gi, targetVal)
        .replace(/__percent/gi, percentVal)
        .replace(/__diff/gi, diffVal);

      const qualified = Function("return " + realStatement)();
      if (qualified === true) {
        found = true;
        break; // Exit loop early if condition is met
      }
    }
    return found;
  },

  async createAward({ dispatch, commit, getters }, payload) {
    console.log(payload);
    const awardsRef = collection(
      doc(db(), "programs", getters.programId),
      "awards"
    );

    try {
      const titleDupQuery = query(
        awardsRef,
        where("awardUppercase", "==", payload.awardUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (titleDupSnapshot.size > 0) {
        throw new Error("Award is already registered.");
      }

      const { id, ...awardInput } = payload; // eslint-disable-line no-unused-vars
      const award = {
        ...awardInput
      };

      const newAwardRef = await addDoc(awardsRef, award);

      commit("createAward", { ...awardInput, id: newAwardRef.id });
      dispatch("setSnackbar", "Award Created.");
    } catch (error) {
      console.error("Error creating award:", error);
      if (error.message === "Award is already registered.") {
        throw error.message;
      } else {
        throw "Error occurred when creating a new award.";
      }
    }
  },

  async updateAward({ dispatch, commit, getters }, payload) {
    const awardsRef = collection(
      doc(db(), "programs", getters.programId),
      "awards"
    );

    try {
      const awardDoc = await getDoc(doc(awardsRef, payload.id));
      const storedAward = awardDoc.data();

      if (!storedAward) {
        throw new Error("Error occurred when fetching the award info");
      }

      const titleDupQuery = query(
        awardsRef,
        where("awardUppercase", "==", payload.awardUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (titleDupSnapshot.size > 0) {
        let duplicated = false;
        titleDupSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });
        if (duplicated) {
          throw new Error("Award is already registered.");
        }
      }

      const { id, ...awardInput } = payload;
      const award = {
        ...awardInput
      };

      await updateDoc(doc(awardsRef, id), award);

      commit("updateAward", payload);
      dispatch("setSnackbar", "Award Updated.");
    } catch (error) {
      console.error("Error updating award:", error);
      if (
        error.message === "Award is already registered." ||
        error.message === "Error occurred when fetching the award info"
      ) {
        throw error.message;
      } else {
        throw "Error occurred when updating an award";
      }
    }
  },

  async deleteAward({ dispatch, commit, getters }, awardId) {
    try {
      await deleteDoc(
        doc(
          collection(doc(db(), "programs", getters.programId), "awards"),
          awardId
        )
      );
      commit("deleteAward", awardId);
      dispatch("setSnackbar", "Award Deleted.");
    } catch (error) {
      console.error("Error deleting award:", error);
      throw "Error occurred when deleting an award";
    }
  }
};

const mutations = {
  setAwards(state, payload) {
    state.awards = payload;
  },

  setAllAwardData(state, payload) {
    state.allAwardData = payload;
  },

  setMemberQualifiedAwardData(state, payload) {
    state.memberQualifiedAwardData = payload;
  },

  setLoadingTable(state, payload) {
    state.loadingAwards = payload;
  },

  createAward(state, payload) {
    state.awards = [...state.awards, payload];
  },

  updateAward(state, payload) {
    state.awards = state.awards.map(item => {
      if (item.id === payload.id) {
        return payload;
      }
      return item;
    });
  },

  deleteAward(state, payload) {
    state.awards = state.awards.filter(item => item.id !== payload);
  }
};

const getters = {
  awards(state) {
    return state.awards;
  },
  allAwardData(state) {
    return state.allAwardData;
  },
  memberQualifiedAwardData(state) {
    return state.memberQualifiedAwardData;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
